import React, { useState } from 'react';
import { useSyncStore } from '../../store/syncStore';
import { formatPriceWithSpaces } from '../../utils/formatNumber';
import { MissingProduct } from '../../types';
import Pagination from '../pagination/Pagination';

interface MissingProductsTableProps {
  warehouseId?: number;
}

const MissingProductsTable = ({ warehouseId = 1 }: MissingProductsTableProps) => {
  const {
    missingProducts,
    isLoadingMissing,
    missingError,
    missingLastFetch,
    fetchMissingProducts,
    clearMissingProducts,
    isDataStale,
  } = useSyncStore();

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // Состояние сортировки
  const [sortConfig, setSortConfig] = useState<{
    key: string | null;
    direction: 'asc' | 'desc';
  }>({
    key: null,
    direction: 'asc'
  });

  // Функция обработки клика по заголовку
  const handleSort = (key: string) => {
    setSortConfig(prevConfig => {
      // Если кликаем по той же колонке, меняем направление
      if (prevConfig.key === key) {
        return {
          key,
          direction: prevConfig.direction === 'asc' ? 'desc' : 'asc'
        };
      }
      // Если кликаем по новой колонке, начинаем с 'asc'
      return {
        key,
        direction: 'asc'
      };
    });
    // Сбрасываем на первую страницу при сортировке
    setCurrentPage(1);
  };

  // Сортировка товаров
  const sortedProducts = React.useMemo(() => {
    if (!sortConfig.key) {
      // По умолчанию: сначала добавленные, потом остальные
      return [...missingProducts].sort((a, b) => {
        if (a.is_added !== b.is_added) {
          return b.is_added ? 1 : -1; // true идет первым
        }
        return a.name.localeCompare(b.name);
      });
    }

    return [...missingProducts].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortConfig.key) {
        case 'status':
          // Специальная логика для статуса
          aValue = a.is_added;
          bValue = b.is_added;
          break;
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'sku':
          aValue = a.sku.toLowerCase();
          bValue = b.sku.toLowerCase();
          break;
        case 'price':
          aValue = a.price;
          bValue = b.price;
          break;
        case 'quantity':
          aValue = a.quantity;
          bValue = b.quantity;
          break;
        case 'warehouse':
          aValue = a.warehouse.name.toLowerCase();
          bValue = b.warehouse.name.toLowerCase();
          break;
        case 'description':
          aValue = (a.description || '').toLowerCase();
          bValue = (b.description || '').toLowerCase();
          break;
        default:
          return 0;
      }

      // Сравнение значений
      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [missingProducts, sortConfig]);

  // Функция для отображения иконки сортировки
  const getSortIcon = (columnKey: string) => {
    if (sortConfig.key !== columnKey) {
      return (
        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }

    if (sortConfig.direction === 'asc') {
      return (
        <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
        </svg>
      );
    } else {
      return (
        <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-4-4m4 4l4-4" />
        </svg>
      );
    }
  };

  const shouldLoadData = !missingProducts.length && !isLoadingMissing;
  const isDataOld = missingProducts.length > 0 && isDataStale('missing', 5);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleLoadData = () => {
    fetchMissingProducts(warehouseId);
  };

  const buildAddUrl = (product: MissingProduct) => {
    const params = new URLSearchParams({
      name: product.name,
      sku: product.sku,
      price: product.price.toString(),
    });
    return `/admin?tab=addProduct&${params.toString()}`;
  };

  // Если нет данных и не загружается - показываем кнопку загрузки
  if (shouldLoadData) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="text-center">
          <div className="text-gray-500 text-lg mb-4">Данные не загружены</div>
          <button
            onClick={handleLoadData}
            className="bg-blue-500 text-white px-6 py-3 rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2 mx-auto"
          >
            <span>🔄</span>
            Загрузить товары
          </button>
        </div>
      </div>
    );
  }

  if (isLoadingMissing) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <span className="ml-2 text-gray-600">Загрузка товаров...</span>
        </div>
      </div>
    );
  }

  if (missingError) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="text-center">
          <div className="text-red-500 text-lg mb-4">Ошибка загрузки</div>
          <p className="text-gray-600 mb-4">{missingError}</p>
          <button
            onClick={handleLoadData}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors"
          >
            Попробовать снова
          </button>
        </div>
      </div>
    );
  }

  if (!missingProducts.length) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="text-center">
          <div className="text-gray-500 text-lg mb-4">Товары не найдены</div>
          <p className="text-gray-600">Все товары со склада уже добавлены в базу данных</p>
        </div>
      </div>
    );
  }

  // Пагинация
  const totalPages = Math.ceil(sortedProducts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentProducts = sortedProducts.slice(startIndex, endIndex);
  console.log(currentProducts);

  return (
    <div className="bg-white rounded-lg shadow-md">
      {/* Заголовок */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800">
                Товары, которых нет в БД
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                💡 Кликните по заголовку колонки для сортировки
              </p>
            </div>
            {missingProducts.length > 0 && (
              <div className="flex items-center gap-4 mt-2 text-sm">
                <span className="text-green-600">
                  ✅ Добавлено: {missingProducts.filter(p => p.is_added).length}
                </span>
                <span className="text-gray-600">
                  ⏳ Не добавлено: {missingProducts.filter(p => !p.is_added).length}
                </span>
                <span className="text-gray-500">
                  📊 Всего: {missingProducts.length}
                </span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-4">
            {isDataOld && (
              <span className="text-yellow-500 text-sm">⚠️ Данные устарели</span>
            )}
            <button
              onClick={handleLoadData}
              className="text-blue-500 hover:text-blue-600 text-sm font-medium"
            >
              Обновить
            </button>
          </div>
        </div>
        <p className="text-sm text-gray-600 mt-1">
          Найдено товаров: {missingProducts.length}
        </p>
      </div>

      {/* Таблица */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('status')}
              >
                <div className="flex items-center gap-2">
                  Статус
                  {getSortIcon('status')}
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('name')}
              >
                <div className="flex items-center gap-2">
                  Название
                  {getSortIcon('name')}
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('sku')}
              >
                <div className="flex items-center gap-2">
                  SKU
                  {getSortIcon('sku')}
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('price')}
              >
                <div className="flex items-center gap-2">
                  Цена
                  {getSortIcon('price')}
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('quantity')}
              >
                <div className="flex items-center gap-2">
                  Количество
                  {getSortIcon('quantity')}
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('warehouse')}
              >
                <div className="flex items-center gap-2">
                  Склад
                  {getSortIcon('warehouse')}
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('description')}
              >
                <div className="flex items-center gap-2">
                  Описание
                  {getSortIcon('description')}
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Действия
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {currentProducts.map((product) => (
              <tr
                key={`${product.id}-${product.prosklad_id}`}
                className={`hover:bg-gray-50 ${product.is_added ? 'bg-green-50' : ''}`}
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    {product.is_added ? (
                      <div className="flex items-center text-green-600">
                        <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        <span className="text-sm font-medium">Добавлен</span>
                      </div>
                    ) : (
                      <div className="flex items-center text-gray-400">
                        <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                        <span className="text-sm">Не добавлен</span>
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">
                    {product.name}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{product.sku}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    {formatPriceWithSpaces(product.price.toString())} ₸
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{product.quantity}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{product.warehouse.name}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900 max-w-xs truncate" title={product.description}>
                    {product.description || '-'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  {product.is_added ? (
                    <span className="text-green-600 bg-green-50 px-3 py-1 rounded-md font-medium">
                      Уже добавлен
                    </span>
                  ) : (
                    <a
                      href={buildAddUrl(product)}
                      className="text-blue-600 hover:text-blue-900 bg-blue-50 hover:bg-blue-100 px-3 py-1 rounded-md transition-colors inline-block"
                    >
                      Добавить
                    </a>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Пагинация */}
      {totalPages > 1 && (
        <div className="px-6 py-4 border-t border-gray-200">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </div>
      )}
    </div>
  );
};

export default MissingProductsTable;